import React, { useEffect, useId, useRef } from 'react'

const CITYSPARK_PORTAL_HOST = 'portal.cityspark.com'
const CITYSPARK_PORTAL_PATH_PATTERN = /^\/PortalScripts\/[A-Za-z0-9_-]+\/?$/i

const getCitySparkPortalUrl = (src: string) => {
  const trimmedSrc = src.trim()

  if (!trimmedSrc) {
    return null
  }

  try {
    return new URL(trimmedSrc.startsWith('//') ? `https:${trimmedSrc}` : trimmedSrc)
  } catch {
    return null
  }
}

declare global {
  interface Window {
    cSparkLoad?: string
  }
}

export const getCitySparkPortalScriptSrc = (html: string = '') => {
  const match = html.match(/<script[^>]+src=["']([^"']*portal\.cityspark\.com\/PortalScripts\/[^"']+)["'][^>]*>/i)
  return match?.[1] ?? null
}

export const isCitySparkPortalScriptSrc = (src?: string | null) => {
  if (!src) {
    return false
  }

  const url = getCitySparkPortalUrl(src)

  return Boolean(
    url &&
      url.protocol === 'https:' &&
      url.hostname.toLowerCase() === CITYSPARK_PORTAL_HOST &&
      CITYSPARK_PORTAL_PATH_PATTERN.test(url.pathname)
  )
}

const CitySparkPortal = ({ src }: { src: string }) => {
  const reactId = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const mountId = `cityspark-portal-${reactId}`
  const selector = `#${mountId}`
  const containerRef = useRef<HTMLDivElement>(null)
  const scriptSrc = src.trim()

  useEffect(() => {
    if (!isCitySparkPortalScriptSrc(scriptSrc)) {
      return
    }

    window.cSparkLoad = selector

    const script = document.createElement('script')
    script.id = `${mountId}-script`
    script.src = scriptSrc
    script.type = 'text/javascript'
    script.async = false

    containerRef.current?.appendChild(script)

    return () => {
      script.remove()
    }
  }, [mountId, scriptSrc, selector])

  if (!isCitySparkPortalScriptSrc(src)) {
    return null
  }

  return (
    <div className='cityspark-portal-embed' ref={containerRef}>
      <div id={mountId} />
      <script
        dangerouslySetInnerHTML={{ __html: `window.cSparkLoad=${JSON.stringify(selector)};` }}
        id={`${mountId}-target`}
        type='text/javascript'
      />
    </div>
  )
}

export default CitySparkPortal
